import { Handle, Position, BaseEdge, EdgeLabelRenderer, useInternalNode } from "@xyflow/react";
import { catColor } from "@/lib/lore";

const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export function PersonNode({ data }) {
  const c = catColor(data.category);
  return (
    <div className={`group flex w-28 flex-col items-center transition-opacity duration-200 ${data.dim ? "opacity-25" : ""}`} data-testid={`rel-node-${data.id}`}>
      <div className={`relative h-16 w-16 overflow-hidden rounded-full border-2 bg-[#13171F] shadow-lg transition-transform duration-150 group-hover:scale-105 ${data.selected ? "ring-4 ring-[#F59E0B]/40" : ""}`}
        style={{ borderColor: c }}>
        {data.image ? <img src={data.image} alt={data.name} className="h-full w-full object-cover" draggable={false} />
          : <div className="flex h-full w-full items-center justify-center font-display text-xl" style={{ color: c }}>{initials(data.name)}</div>}
      </div>
      <div className="mt-1.5 max-w-[7rem] text-center text-[13px] leading-tight text-[#F3EBDD]">{data.name}</div>
      <Handle type="target" position={Position.Top} className="!border-0 !bg-transparent !opacity-0" style={{ top: 32, left: 56, width: 2, height: 2, minWidth: 0, minHeight: 0 }} isConnectable={data.canEdit} />
      {data.canEdit ? (
        <Handle type="source" position={Position.Right} title="Drag to another person to link them"
          className="!h-4 !w-4 !border-2 !border-[#0B0E14] !bg-[#F59E0B] opacity-0 transition-opacity group-hover:opacity-100" style={{ top: 32, right: 14 }} />
      ) : (
        <Handle type="source" position={Position.Right} isConnectable={false} className="!border-0 !bg-transparent !opacity-0 pointer-events-none"
          style={{ top: 32, left: 56, width: 2, height: 2, minWidth: 0, minHeight: 0 }} />
      )}
    </div>
  );
}

function center(node) {
  const { x, y } = node.internals.positionAbsolute;
  return { x: x + 56, y: y + 32 };
}

export function RelEdge({ id, source, target, data, selected }) {
  const s = useInternalNode(source), t = useInternalNode(target);
  if (!s || !t) return null;
  const a = center(s), b = center(t);
  const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
  const off = (data.offset || 0) * 34;
  const cx = (a.x + b.x) / 2 - (dy / len) * off, cy = (a.y + b.y) / 2 + (dx / len) * off;
  const path = `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
  const lx = (a.x + 2 * cx + b.x) / 4, ly = (a.y + 2 * cy + b.y) / 4;
  const color = data.color;
  return (
    <>
      <BaseEdge id={id} path={path} interactionWidth={18}
        style={{ stroke: color, strokeWidth: selected ? 4 : data.suggested ? 1.5 : 2.5, strokeDasharray: data.suggested ? "6 6" : undefined, opacity: data.dim ? 0.12 : data.suggested ? 0.6 : 0.95, transition: "opacity .2s" }} />
      <EdgeLabelRenderer>
        <div className={`nodrag nopan pointer-events-auto absolute cursor-pointer rounded-full border px-2 py-0.5 text-[11px] transition-opacity ${data.dim ? "opacity-10" : ""}`}
          style={{ transform: `translate(-50%, -50%) translate(${lx}px, ${ly}px)`, borderColor: `${color}88`, background: "#0F131Aee", color, borderStyle: data.suggested ? "dashed" : "solid" }}
          onClick={data.onClick} data-testid={`rel-edge-label-${id}`}>
          {data.label}{data.status ? <span className="text-[#A8A090]"> · {data.status}</span> : null}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
